import { defineStore, acceptHMRUpdate } from 'pinia'
import { useMaps } from './maps'
import { toRaw } from 'vue'
import { getAuthorization, getProjects, getFeaturesFromProject } from '../scripts/trimble'
import moment from 'moment'

// Convert unreadable keys from Trimble to readable keys
const JSON_TO_CAMEL_MAP = {
    'text2': 'comments',
    'text4': 'farmName',
    'text5': 'whyThisPart',
    'text3': 'personDoingMeasurement',
    'general': 'numberOfIslandsWithinField',
    'yesno': 'wasSomeoneFromFarmWithYou',
    'When_is_fi': 'whenIsFieldNeededOnMachine',
    'Headland_C': 'headlandConfiguration',
    'AB_orienta': 'abOrientation',
    'Comment': 'comment',
    'Images': 'images',
    'Area01': 'area',
    'estimatedaccuracy': 'estimatedAccuracy',
    'increment': 'uniqueId',
    'Device': 'device',
    'deviceid': 'deviceId',
    'devicetype': 'deviceType',
    'Correction': 'correctionStatus',
    'correctionsource': 'correctionSource',
    'correctionstatus': 'correctionStatus',
    'pdop': 'pdop',
    'HDOP': 'hdop',
    'choice': 'choice',
    'choice2': 'choice2',
    'choice3': 'headlandConfiguration',
    'choice4': 'abOrientation',
    'creationdatetime': 'timeDateCreated',
    'geometryarea': 'geometryArea',
    'geometrycapturetype': 'geometryCaptureType',
    'text': 'fieldNameAndFieldId',
    'date': 'whenIsFieldNeededOnMachine',
    'name': 'name',
    // From here are keys from downloaded geometries (Shapefile) from Nexat Planner and uploaded to Trimble
    'id': 'fieldId',
    'active': 'wasActive',
    'config': 'headlandConfig',
    'created': 'timeDateCreated',
    'farm_id': 'farmId',
    'changed_by': 'changedBy',
    'field_name': 'fieldName',
    'generator_': 'generatorVersion',
    'internal_c': 'internalComment',
    'last_chang': 'lastChanged',
    'track_widt': 'trackWidth',
    'user_comme': 'userComment'
    // to here
}

// Convert readable keys to readable labels for the drawer
export const GEOMETRY_LABELS = {
    fieldName: 'Field name',
    farmName: 'Farm name',
    personDoingMeasurement: 'Person doing measurement',
    numberOfIslandsWithinField: 'Number of islands within field',
    wasSomeoneFromFarmWithYou: 'Was someone from farm with you?',
    whenIsFieldNeededOnMachine: 'When is field needed on machine?',
    headlandConfiguration: 'Headland configuration',
    abOrientation: 'AB orientation',
    comment: 'Comment',
    images: 'Images',
    area: 'Area',
    estimatedAccuracy: 'Estimated accuracy',
    uniqueId: 'Unique ID (internal)',
    device: 'Device',
    deviceId: 'Device ID',
    deviceType: 'Device',
    timeDateCreated: 'Date created',
    correctionStatus: 'Correction status',
    correctionSource: 'Correction source',
    pdop: 'PDOP',
    hdop: 'HDOP',
    choice: 'Type',
    choice2: 'Coordinate system',
    choice3: 'Headland configuration',
    choice4: 'AB orientation',
    text: 'Name',
    comments: 'Comments',
    image: 'Image',
    yesno: 'Was someone from farm with you?',
    geometryArea: 'Area',
    geometryCaptureType: 'Capture method',
    fieldNameAndFieldId: 'Field name & Field ID',
    whyThisPart: 'Why was this part (re-)measured?',
    name: 'Name',
    // From here are keys from downloaded geometries (Shapefile) from Nexat Planner and uploaded to Trimble
    fieldId: 'Field ID',
    wasActive: 'Was active',
    headlandConfig: 'Headland configuration',
    farmId: 'Farm ID',
    changedBy: 'Changed by',
    generatorVersion: 'Generator version',
    internalComment: 'Internal comment',
    lastChanged: 'Last changed',
    trackWidth: 'Track width',
    userComment: 'User comment'
    // to here
}

// Layers types for "Filter by type" dropdown
export const TRIMBLE_LAYER_TYPES = [
    {
        id: 'border_point',
        baseName: 'Border Point',
        displayName: 'Border Point (Point)',
        // layerIds: ['S3zQ3SZl2FI', 'vHRbeF379vA', 'V1W_6BjSxc0'],
        layerIds: ['S3zQ3SZl2FI'],
        icon: 'pi pi-map-marker'
    },
    {
        id: 'border_line',
        baseName: 'Border Line',
        displayName: 'Border Line (Line)',
        layerIds: ['GZn2JsAhpaE'],
        color: '#000000'
    },
    {
        id: 'field_entry_point',
        baseName: 'Field Entry Point',
        displayName: 'Field Entry Point (Line)',
        layerIds: ['oW7AxTDXSoo'],
        color: '#0000FF'
    },
    {
        id: 'wish_ab_direction',
        baseName: 'Wish AB-direction',
        displayName: 'Wish AB-direction (Line)',
        layerIds: ['gHFyfV_YswQ'],
        color: '#00ff00'
    },
    {
        id: 'field_boundary',
        baseName: 'Field Boundary',
        displayName: 'Field Boundary (Polygon)',
        // layerIds: ['mt0GVTAsR_8', 'WK6mC5FzkY4'],
        layerIds: ['mt0GVTAsR_8'],
        color: '#FF0000'
    },
    {
        id: 'island_within_field',
        baseName: 'Island or CutOut within Field',
        displayName: 'Island or CutOut within Field (Polygon)',
        layerIds: ['b4vmDsqTF9k'],
        color: '#FFA500'
    },
    // {
    //     id: 'road_line',
    //     baseName: 'Road Line',
    //     displayName: 'Road Line (Line)',
    //     layerIds: ['JsvLVSsvXt0'],
    //     color: '#00FFFF'
    // },
    // {
    //     id: 'unknown',
    //     baseName: 'Unknown',
    //     displayName: 'Unknown (Unknown)',
    //     layerIds: ['1y-6UzkeMyw', 'M-9ANSyFY_M', 'qi5cFTiLkbk', 'wGl9bSlKy_o', 'wI6ELtmgewE'],
    //     color: '#FF00FF'
    // }
]

export const useTrimbleStore = defineStore('trimble', {
    state: () => ({
        newUploads: 3,
        fieldBorder: [],
        highlightedMapObject: null,
        drawerVisible: false,
        currentGeometry: null,
        features: [],
        currentFeature: null,
        projects: [],
        selectedProjectId: null,
        lastCheckTime: null,
        isAuthenticated: false,
        isLoading: false,
        searchQuery: '',
        startDate: null,
        endDate: null,
        errorMessage: null,
        selectedFeatureTypes: TRIMBLE_LAYER_TYPES.map(t => t.id),
        isUnmounted: false
    }),
    getters: {
        currentCategoryName() {
            if (!this.currentFeature?.metadata?.common_layerId) return 'Unknown category'

            const layerId = this.currentFeature.metadata.common_layerId
            const typeDef = TRIMBLE_LAYER_TYPES.find(t => t.layerIds.includes(layerId))
            return typeDef?.baseName || 'Unknown category'
        },
        featureTypeOptionsForMultiSelect() {
            return TRIMBLE_LAYER_TYPES.map(t => ({
                id: t.id,
                name: t.displayName,
                color: t.color,
                icon: t.icon
            }))
        },
        // All feature type ids
        allFeatureTypeIds() {
            return TRIMBLE_LAYER_TYPES.map(t => t.id)
        },
        filteredFeatures() {
            let filtered = this.features

            // Filter by search query
            if (this.searchQuery) {
                filtered = filtered.filter(feature => 
                    feature.properties?.text?.toLowerCase().includes(this.searchQuery.toLowerCase())
                )
            }

            // Filter by date range (only if both dates are set)
            if (this.startDate && this.endDate) {
                filtered = filtered.filter(feature => this.isFeatureInDateRange(feature))
            }

            return filtered
        }
    },
    actions: {
        // Check if feature is within selected date range (uses only date part, ignores time)
        isFeatureInDateRange(feature) {
            if (!this.startDate || !this.endDate) return true

            const featureDate = feature.properties?.creationdatetime || feature.metadata?.collection_utc || feature.metadata?.common_updatedUtc || feature.properties?.created
            if (!featureDate) return false

            const dateStr = new Date(featureDate).toISOString().split('T')[0]
            const startStr = new Date(this.startDate).toISOString().split('T')[0]
            const endStr = new Date(this.endDate).toISOString().split('T')[0]

            return dateStr >= startStr && dateStr <= endStr
        },
        // Check if a feature belongs to a given type (by type id from TRIMBLE_LAYER_TYPES)
        isFeatureOfType(feature, typeId) {
            if (!typeId) return true

            const layerId = feature?.metadata?.common_layerId
            if (!layerId) return false

            const typeDef = TRIMBLE_LAYER_TYPES.find(t => t.id === typeId)
            if (!typeDef) return false

            return typeDef.layerIds.includes(layerId)
        },
        // Check if a feature belongs to any of the given type ids (array). Empty array = none.
        isFeatureOfAnyType(feature, typeIds) {
            if (!typeIds || !Array.isArray(typeIds) || typeIds.length === 0) return false

            return typeIds.some(typeId => this.isFeatureOfType(feature, typeId))
        },
        // Get type id (from TRIMBLE_LAYER_TYPES) for a feature based on its layerId
        getFeatureTypeId(feature) {
            if (!feature?.metadata?.common_layerId) return null

            const layerId = feature.metadata.common_layerId
            const typeDef = TRIMBLE_LAYER_TYPES.find(t => t.layerIds.includes(layerId))
            return typeDef?.id || null
        },
        async checkAuthentication() {
            try {
                this.isLoading = true
                this.errorMessage = null
                const res = await getAuthorization()
                if (res.problem === 'NETWORK_ERROR') {
                    this.isAuthenticated = false
                    this.selectedProjectId = null
                    this.features = []
                    this.errorMessage = 'Network error. Please try again later.'
                    this.isLoading = false
                    this.clearFieldBorder()
                    return
                }
                return res
            } catch (error) {
                console.error('Error checking authentication:', error)
                this.errorMessage = 'Error checking authentication. Please try again later.'
                this.isAuthenticated = false
                this.selectedProjectId = null
                this.features = []
                this.isLoading = false
                this.clearFieldBorder()
                return
            }
        },
        setFeatures(payload) {
            // Sort features by creation date
            this.features = Array.isArray(payload) ? payload : (payload?.features || []).sort((a, b) => {
                const dateA = a.properties?.creationdatetime || a.metadata?.collection_utc || a.metadata?.common_updatedUtc || a.properties?.created
                const dateB = b.properties?.creationdatetime || b.metadata?.collection_utc || b.metadata?.common_updatedUtc || b.properties?.created
                return new Date(dateB) - new Date(dateA)
            })
        },
        // Extract coords [lng, lat] from geometry (Polygon, LineString or Point)
        getCoordinatesFromGeometry(geometry) {
            if (!geometry || !geometry.coordinates) return []

            const { type, coordinates } = geometry
            if (type === 'Polygon') return coordinates[0] || []
            if (type === 'LineString') return coordinates || []
            if (type === 'Point') return [coordinates]

            return []
        },
        // Center map on feature + open drawer + scroll to card in list
        centerMapOnFeature(feature) {
            const mapStore = useMaps()
            if (!mapStore.map || !mapStore.google) return

            const coords = this.getCoordinatesFromGeometry(feature.geometry)
            if (coords.length === 0) return

            const path = coords.map(coord => ({ lng: coord[0], lat: coord[1] }))
            const geomType = feature.geometry?.type

            // Reset highlight
            this.clearHighlight()

            // Find map object for this feature and highlight the line
            const mapObj = this.fieldBorder.find(o => o.featureId === feature.id)
            if (mapObj && mapObj.setOptions && mapObj.highlightStrokeWeight) {
                mapObj.setOptions({ strokeWeight: mapObj.highlightStrokeWeight })
                this.highlightedMapObject = mapObj
            }

            // Center map on feature
            if (geomType === 'Point') {
                mapStore.map.setCenter(path[0])
                mapStore.map.setZoom(Math.max(mapStore.map.getZoom() || 15, 15))
            } else {
                const bounds = new mapStore.google.maps.LatLngBounds()
                path.forEach(p => bounds.extend(p))
                mapStore.map.fitBounds(bounds)
            }

            // Update current feature and open drawer
            this.drawerVisible = true
            this.currentFeature = feature
            this.setCurrentGeometry(feature.properties || {})

            // Scroll to card in list
            this.scrollToFeatureCard(feature.id)
        },
        clearHighlight() {
            if (this.highlightedMapObject && this.highlightedMapObject.setOptions && this.highlightedMapObject.normalStrokeWeight != null) {
                this.highlightedMapObject.setOptions({ strokeWeight: this.highlightedMapObject.normalStrokeWeight })
            }
            this.highlightedMapObject = null
            this.drawerVisible = false
            this.currentFeature = null
            this.currentGeometry = null
        },
        scrollToFeatureCard(featureId) {
            const cardElement = document.getElementById(`trimble-feature-${featureId}`)
            if (cardElement) {
                cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
        },
        drawAllGeometries() {
            // Clear existing geometries first
            this.clearFieldBorder()

            // Start from all features
            let featuresToDraw = this.features

            // Apply date filter if both dates are set
            if (this.startDate && this.endDate) {
                featuresToDraw = featuresToDraw.filter(f => this.isFeatureInDateRange(f))
            }

            // Type filter: Empty array [] = draw nothing. Otherwise filter by selected types (selectedFeatureTypes)
            const ids = this.selectedFeatureTypes
            if (ids.length === 0) {
                featuresToDraw = []
            } else {
                featuresToDraw = featuresToDraw.filter(f => this.isFeatureOfAnyType(f, ids))
            }

            featuresToDraw.forEach(feature => {
                const coords = this.getCoordinatesFromGeometry(feature.geometry)
                if (coords.length === 0) return

                const path = coords.map(coord => ({ lng: coord[0], lat: coord[1] }))
                const geomType = feature.geometry?.type
                this.drawGeometry(path, geomType, feature, false) // Don't center when drawing all
            })
        },
        drawGeometry(path, geomType, feature, shouldCenter = false) {
            const mapStore = useMaps()
            let mapObject = null

            const strokeWeightNormal = 2
            const strokeWeightHighlight = 5

            if (geomType === 'Polygon') {
                const polygon = new mapStore.google.maps.Polygon({
                    paths: path,
                    fillColor: this.getPolygonLayerColor(feature.metadata.common_layerId),
                    fillOpacity: 0.1,
                    strokeColor: this.getPolygonLayerColor(feature.metadata.common_layerId),
                    strokeOpacity: 1,
                    strokeWeight: strokeWeightNormal,
                    clickable: true,
                    zIndex: feature.metadata.common_layerId === 'b4vmDsqTF9k' ? 1001 : 100
                })
                polygon.setMap(mapStore.map)
                mapObject = toRaw(polygon)
                mapObject.normalStrokeWeight = strokeWeightNormal
                mapObject.highlightStrokeWeight = strokeWeightHighlight
                this.fieldBorder.push(mapObject)
            } else if (geomType === 'Point') {
                const marker = new mapStore.google.maps.marker.AdvancedMarkerElement({
                    position: path[0],
                    map: toRaw(mapStore.map),
                    title: feature.properties?.text || '',
                    gmpClickable: true
                })
                mapObject = toRaw(marker)
                this.fieldBorder.push(mapObject)
            } else {
                const polyline = new mapStore.google.maps.Polyline({
                    path,
                    strokeColor: this.getPolylineLayerColor(feature.metadata.common_layerId),
                    strokeOpacity: 1,
                    strokeWeight: strokeWeightNormal,
                    clickable: true,
                    zIndex: 1002
                })
                polyline.setMap(mapStore.map)
                mapObject = toRaw(polyline)
                mapObject.normalStrokeWeight = strokeWeightNormal
                mapObject.highlightStrokeWeight = strokeWeightHighlight
                this.fieldBorder.push(mapObject)
            }

            // Store feature reference on map object for click handling
            if (mapObject) {
                mapObject.featureId = feature.id
                mapObject.feature = feature
                
                // Add click listener (different for AdvancedMarkerElement vs Polygon/Polyline)
                if (geomType === 'Point') {
                    // AdvancedMarkerElement uses addEventListener with gmp-click
                    mapObject.addEventListener('gmp-click', () => {
                        this.centerMapOnFeature(feature)
                    })
                } else {
                    // Polygon and Polyline use google.maps.event.addListener
                    mapStore.google.maps.event.addListener(mapObject, 'click', () => {
                        this.centerMapOnFeature(feature)
                    })
                }
            }

            // Only center if explicitly requested (when drawing all geometries, don't center)
            if (shouldCenter) {
                if (geomType === 'Point') {
                    mapStore.map.setCenter(path[0])
                    mapStore.map.setZoom(Math.max(mapStore.map.getZoom() || 15, 15))
                } else {
                    const bounds = new mapStore.google.maps.LatLngBounds()
                    path.forEach(p => bounds.extend(p))
                    mapStore.map.fitBounds(bounds)
                }
            }
        },
        getPolygonLayerColor(layerId) {
            switch (layerId) {
                case 'b4vmDsqTF9k':
                    return '#FFA500'
                case 'mt0GVTAsR_8':
                // case 'WK6mC5FzkY4':
                    return '#FF0000'
                // case '1y-6UzkeMyw': 
                // case 'M-9ANSyFY_M': 
                // case 'qi5cFTiLkbk': 
                // case 'wGl9bSlKy_o': 
                // case 'wI6ELtmgewE':
                //     return '#FF00FF'
                default:
                    return '#71717a'
            }
        },
        getPolylineLayerColor(layerId) {
            switch (layerId) {
                case 'oW7AxTDXSoo':
                    return '#0000FF'
                case 'gHFyfV_YswQ':
                    return '#00ff00'
                // case 'JsvLVSsvXt0':
                //     return '#00FFFF'
                case 'GZn2JsAhpaE':
                    return '#000000'
            }

            return '#FF00FF'
        },
        // Layer color for SVG
        getFeatureLayerColor(feature) {
            if (!feature?.metadata?.common_layerId) {
                return '#71717a'
            }

            const layerId = feature.metadata.common_layerId
            const geomType = feature.geometry?.type
            if (geomType === 'Polygon') {
                return this.getPolygonLayerColor(layerId)
            }

            if (geomType === 'LineString') {
                return this.getPolylineLayerColor(layerId)
            }

            return this.getPolylineLayerColor(layerId)
        },
        clearFieldBorder() {
            const mapStore = useMaps()
            this.fieldBorder.forEach(mapObject => {
                const raw = toRaw(mapObject)
                // Remove listeners before removing from map
                if (raw.featureId && mapStore.google) {
                    try {
                        mapStore.google.maps.event.clearInstanceListeners(raw)
                    } catch (e) {
                        console.warn('Could not clear listeners for map object:', e)
                    }
                }
                raw.setMap(null)
            })
            this.fieldBorder = []
            this.highlightedMapObject = null
            this.drawerVisible = false
            this.currentFeature = null
            this.currentGeometry = null
        },
        setCurrentGeometry(rawProperties) {
            this.currentGeometry = Object.fromEntries(
                Object.entries(rawProperties || {}).map(([key, value]) => [JSON_TO_CAMEL_MAP[key] || key, value])
            )
        },
        async getProjects() {
            if (this.isUnmounted) return

            this.isLoading = true
            try {
                const projects = await getProjects()
                if (this.isUnmounted) return

                if (projects.data?.reason === 'unauthorized') {
                    this.isAuthenticated = false
                    this.selectedProjectId = null
                    this.features = []
                    this.isLoading = false
                    this.clearFieldBorder()
                    return
                } else if (projects.problem === 'NETWORK_ERROR') {
                    this.errorMessage = 'Network error. Please try again later.'
                    this.isAuthenticated = false
                    this.selectedProjectId = null
                    this.features = []
                    this.isLoading = false
                    this.clearFieldBorder()
                    return
                }
                this.projects = Array.isArray(projects) ? projects : []
                
                // Auto-select if only one project
                if (this.projects.length === 1) {
                    this.selectedProjectId = this.projects[0].id
                    await this.getFeaturesFromProject(this.projects[0].id)
                } else if (this.projects.length === 0) {
                    this.selectedProjectId = null
                    this.features = []
                    this.isLoading = false
                    this.clearFieldBorder()
                }
            } catch (error) {
                console.error('Error fetching projects:', error)
                this.projects = []
                this.selectedProjectId = null
            } finally {
                this.isLoading = false
            }
        },
        async getFeaturesFromProject(projectId) {
            if (!projectId || this.isUnmounted) return
            
            try {
                this.isLoading = true
                const featureCollection = await getFeaturesFromProject(projectId)
                if (this.isUnmounted) return
                
                if (featureCollection.status === 401) {
                    this.isAuthenticated = false
                    this.selectedProjectId = null
                    this.features = []
                    this.isLoading = false
                    this.clearFieldBorder()
                } else {
                    this.isAuthenticated = true
                    this.selectedProjectId = projectId
                    if (featureCollection?.features) {
                        this.setFeatures(featureCollection)
                        this.drawAllGeometries()
                    }
                    this.lastCheckTime = moment().format('HH:mm')
                }
            } catch (error) {
                console.error('Error reloading features:', error)
                this.errorMessage = 'Error reloading measurements. Please try again later.'
                this.isAuthenticated = false
                this.selectedProjectId = null
                this.features = []
                this.isLoading = false
                this.clearFieldBorder()
            } finally {
                this.isLoading = false
            }
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTrimbleStore, import.meta.hot))
}
